import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GitBranch, AlertTriangle, Loader2, Plus, Trash2, ChevronDown, Search, Check,
  FlaskConical, Play, Wand2,
} from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { editIntentTemplateDraft, listIntentMatcherSkillCatalog, dryRunIntentRego } from '../../../services/api';
import { regoVerdictView, REGO_TONE_COLORS, regoConflictHint } from '../../../utils/regoVerdict';

/**
 * Intent Profiles Catalog v1.0 — Phase 1E.2
 *
 * Edit a DRAFT version's payload: allowed skill matchers, max autonomy
 * ceiling, requires-approval flag. APPROVED/DEPRECATED versions are
 * immutable (backend returns 409); the Edit button is hidden on those
 * versions upstream so this slideout is only opened against DRAFT.
 *
 * <h3>Matcher editor</h3>
 * <p>Each matcher row carries (mode, value). Supported modes mirror the
 * backend constants (IntentTemplateVersion.MatcherMode):</p>
 * <ul>
 *   <li><b>explicit-skill</b> — value is a skill UUID. Resolved against
 *       the binding agent's skills at bind time.</li>
 *   <li><b>skill-name</b> — value is a literal skill name. Matches
 *       any agent skill with that exact name at bind time.</li>
 *   <li><b>skill-glob</b> — value is a glob pattern (e.g. "lookup_*").
 *       Matches agent skill names by glob at bind time.</li>
 * </ul>
 *
 * <p>Phase 2B-i upgrades the matcher row from raw inputs to a guided picker:
 * <ul>
 *   <li><b>explicit-skill</b> now uses a searchable combobox sourced from
 *       {@code GET /api/v1/intent-templates/skill-catalog}. The author
 *       sees "send_email (Agent A)" and the matcher stores the underlying
 *       skill UUID. Pre-flight UUID-exists check at save-time prevents
 *       authoring a matcher referencing a deleted skill.</li>
 *   <li><b>skill-name</b> shows a type-ahead dropdown of existing skill
 *       names from the catalog. The author can still type a new literal
 *       (the backend accepts it; rule #2 ensures the matcher freezes at
 *       bind time against the agent's actual skills).</li>
 *   <li><b>skill-glob</b> stays free-text but adds a live "matches N
 *       skill(s)" hint computed client-side against the catalog. Helps
 *       authors validate "lookup_*" against reality before submission.</li>
 * </ul>
 * The catalog is loaded once when the slideout opens and reused across
 * all matcher rows.</p>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li>{@code version} — the IntentTemplateVersion to edit. null closes the slideout.</li>
 *   <li>{@code templateDisplayName} — used in the header for context.</li>
 *   <li>{@code onClose} — callback when user dismisses.</li>
 *   <li>{@code onSaved} — callback when PATCH succeeds. Parent reloads.</li>
 * </ul>
 *
 * <h3>Concurrency (Phase 2A — hard 412 + inline banner)</h3>
 * <p>The parent (IntentTemplateDetailSlideout) passes the template's
 * current etag in via {@code templateEtag}. Save sends that as
 * {@code If-Match}; the backend returns {@code 412 Precondition Failed}
 * if another author has modified the template since the parent loaded
 * it. On 412 we:
 * <ol>
 *   <li>Show a dedicated conflict banner inside this slideout (amber,
 *       distinct from generic red errors so the user understands this
 *       is not a validation problem).</li>
 *   <li>Disable the Save button so the user can't repeatedly retry
 *       against a known-stale etag.</li>
 *   <li>Fire {@code onEtagConflict} so the parent ALSO shows its
 *       conflict banner + disables its own action buttons.</li>
 *   <li>Keep the user's in-flight edits intact — they can Cancel to
 *       close, reopen from the freshly-reloaded parent, and re-apply
 *       their changes against the latest state.</li>
 * </ol>
 *
 * <p>If {@code templateEtag} is not supplied (legacy callers), we don't
 * send {@code If-Match} and the backend's optional-precondition path
 * preserves the pre-Phase-2A last-write-wins semantics.</p>
 */
export function EditIntentTemplateVersionSlideout({
  version, templateDisplayName, templateEtag, onClose, onSaved, onEtagConflict,
}) {
  const isOpen = !!version;

  const [matchers, setMatchers] = useState([]);
  const [maxAutonomy, setMaxAutonomy] = useState('');
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [etagConflict, setEtagConflict] = useState(false);

  // J5 — authored intent Rego (fine gate) + dry-run simulation state.
  const [intentRego, setIntentRego] = useState('');
  const [dryInput, setDryInput] = useState({ skill: '', dataClass: '', autonomy: '' });
  const [dryResult, setDryResult] = useState(null);
  const [drying, setDrying] = useState(false);

  // Phase 2B-i — skill catalog. Loaded once per slideout-open. Stays in
  // memory while the slideout is up; reset when a different version is
  // opened. We tolerate catalog-load failure (offer raw inputs in that
  // case) rather than blocking the entire edit form on a single GET.
  const [skillCatalog, setSkillCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogLoadError, setCatalogLoadError] = useState(null);

  // O(1) lookup index: skillId → catalog row. Recomputed only when
  // skillCatalog changes (not on every keystroke). Used by the
  // explicit-skill resolvability check at save time and by the picker
  // to render the friendly name for an already-bound UUID.
  const skillIndexById = useMemo(() => {
    const m = new Map();
    for (const s of skillCatalog) if (s?.id) m.set(s.id, s);
    return m;
  }, [skillCatalog]);

  // Initialize / reset form when a new version is opened
  useEffect(() => {
    if (!version) return;
    const initialMatchers = Array.isArray(version.allowedSkillMatchers)
      ? version.allowedSkillMatchers
      : [];
    setMatchers(initialMatchers.map(toFormRow));
    setMaxAutonomy(version.maxAutonomy || '');
    setRequiresApproval(!!version.requiresApproval);
    setIntentRego(version.intentRego || '');
    setDryResult(null);
    setDryInput({ skill: '', dataClass: '', autonomy: '' });
    setSubmitting(false);
    setErrorMsg(null);
    setEtagConflict(false);
  }, [version]);

  // Load the skill catalog on every open. Tenant-sized lists are small
  // (low hundreds typically) so a fresh load per session is cheap and
  // sidesteps "what if a skill was added since last open" staleness.
  // Cancelled cleanly via an `active` flag on unmount/version-change.
  useEffect(() => {
    if (!version) return;
    let active = true;
    setCatalogLoading(true);
    setCatalogLoadError(null);
    (async () => {
      const res = await listIntentMatcherSkillCatalog();
      if (!active) return;
      setCatalogLoading(false);
      if (res?.ok === false) {
        setCatalogLoadError(res.error || 'Failed to load skill catalog');
        setSkillCatalog([]);
        return;
      }
      const data = res?.data ?? res;
      setSkillCatalog(Array.isArray(data) ? data : []);
    })();
    return () => { active = false; };
  }, [version]);

  const addMatcher = () => {
    setMatchers((prev) => [...prev, { mode: 'skill-name', value: '' }]);
  };

  const removeMatcher = (idx) => {
    setMatchers((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateMatcher = (idx, patch) => {
    setMatchers((prev) => prev.map((m, i) => i === idx ? { ...m, ...patch } : m));
  };

  const handleSave = async () => {
    // Strip empty rows; trim values.
    const cleaned = matchers
      .map((m) => ({ mode: m.mode, value: (m.value || '').trim() }))
      .filter((m) => m.value !== '');

    if (cleaned.length === 0) {
      setErrorMsg('Add at least one skill matcher before saving (an empty matcher list means the version cannot be submitted for review).');
      return;
    }

    // Phase 2B-i — explicit-skill validation upgraded:
    //   (1) value must be a UUID (was: only check),
    //   (2) UUID must resolve to an actual skill in the caller's catalog.
    // This catches the common authoring mistake of pasting a stale UUID
    // from a deleted skill — backend would accept it silently (matcher is
    // a spec, no FK), then every bind would resolve to an empty set for
    // that row, surfacing only at runtime as "intent matches nothing".
    //
    // If the catalog failed to load we degrade to UUID-syntax-only
    // checking + a soft inline warning, rather than blocking edits
    // entirely on a single GET failure.
    for (let i = 0; i < cleaned.length; i++) {
      const m = cleaned[i];
      if (m.mode !== 'explicit-skill') continue;
      if (!isUuid(m.value)) {
        setErrorMsg(`Matcher ${i + 1}: explicit-skill mode requires a UUID value.`);
        return;
      }
      if (skillCatalog.length > 0 && !skillIndexById.has(m.value)) {
        setErrorMsg(`Matcher ${i + 1}: explicit-skill UUID does not match any skill visible in your scope. The skill may have been deleted, or it lives outside your tenant. Pick from the dropdown or remove this matcher.`);
        return;
      }
    }

    setSubmitting(true);
    setErrorMsg(null);
    setEtagConflict(false);
    const res = await editIntentTemplateDraft(
      version.id,
      {
        skillMatchers: cleaned,
        // connectors not edited in v1 — pass null (BE preserves existing).
        connectors: null,
        maxAutonomy: maxAutonomy.trim() === '' ? null : maxAutonomy,
        requiresApproval,
        // J5 — author the fine-gate Rego. Sent as-is; a blank string clears it
        // on the backend (revert to coarse-only DRAFT).
        intentRego,
      },
      templateEtag, // Phase 2A — If-Match precondition (omitted if parent didn't pass it)
    );
    setSubmitting(false);
    // 412 path — DO NOT close the slideout. The user's in-flight edits
    // are preserved so they can Cancel-and-reopen against fresh state.
    if (res?.status === 412 || res?.etagMismatch) {
      setEtagConflict(true);
      // Tell the parent so its catalog/detail views also surface the
      // conflict + disable their action buttons.
      if (onEtagConflict) onEtagConflict();
      return;
    }
    if (res?.ok === false) {
      setErrorMsg(res.error || 'Failed to save draft version');
      return;
    }
    // Forward the freshly-regenerated etag from the response so the parent
    // can chain its next mutation without a full reload round-trip.
    if (onSaved) onSaved(res?.etag ?? null);
    if (onClose) onClose();
  };

  // J5 — scaffold the canonical agentlets.intent package (only into an empty
  // editor, so we never clobber authored policy).
  const scaffoldRego = () => {
    if (!intentRego || intentRego.trim() === '') setIntentRego(REGO_SCAFFOLD);
  };

  // J5 — run the current Rego text against a sample input via OPA. Nothing is
  // persisted. allowed_skills is derived from this version's skill-name matchers
  // so the simulation reflects what the runtime fine gate would actually see.
  const runDryRun = async () => {
    setDrying(true);
    setDryResult(null);
    const allowed = matchers
      .filter((m) => m.mode === 'skill-name' && (m.value || '').trim() !== '')
      .map((m) => m.value.trim());
    const sample = {
      skill: dryInput.skill.trim(),
      allowed_skills: allowed,
      intent: templateDisplayName || '',
      autonomy: dryInput.autonomy || maxAutonomy || '',
      data_class: dryInput.dataClass.trim() || null,
    };
    const res = await dryRunIntentRego(version.id, { rego: intentRego, input: sample });
    setDrying(false);
    if (res?.ok === false) {
      setDryResult({ kind: 'ERROR', reason: res.error || 'Dry-run failed' });
      return;
    }
    const data = res?.data ?? res;
    setDryResult({ kind: data?.kind || 'UNKNOWN', failure: data?.failure, reason: data?.reason || '' });
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={620}
      ariaLabel="Edit intent template draft version"
      header={
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--v2-border-default)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <GitBranch size={12} />
            <span>Edit draft version</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--v2-text-primary)', marginTop: 6 }}>
            {templateDisplayName || 'Template'} · v{version?.versionNumber ?? '?'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginTop: 2 }}>
            Once submitted for review and APPROVED, this version is immutable. Edits after
            approval require creating v(N+1).
          </div>
        </div>
      }
      footer={
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--v2-border-default)', background: 'var(--v2-bg-muted)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="primary" size="sm" onClick={handleSave}
            disabled={submitting || etagConflict}
            title={etagConflict
              ? 'This template was edited by another author. Close this panel, reload the template, then re-apply your edits.'
              : undefined}
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save draft'}
          </Button>
        </div>
      }
    >
      <div style={{ padding: '20px 24px' }}>
        {etagConflict && (
          <div role="alert" style={{
            display: 'flex', alignItems: 'flex-start', gap: 10,
            padding: '12px 14px', marginBottom: 16,
            color: 'var(--v2-warning)', background: 'var(--v2-warning-soft)', border: '1px solid var(--v2-warning)',
            borderRadius: 6, fontSize: 13, lineHeight: 1.45,
          }}>
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                Another author edited this template
              </div>
              <div>
                Your draft was NOT saved. Close this panel (Cancel), reload
                the template from the catalog list, then re-open Edit to
                re-apply your changes on top of the latest state. Your
                in-flight edits below are preserved for now so you can copy
                them if needed.
              </div>
            </div>
          </div>
        )}
        {errorMsg && <InfoBanner kind="error">{errorMsg}</InfoBanner>}

        <SectionLabel>Allowed skill matchers</SectionLabel>
        <p style={{ fontSize: 12, color: 'var(--v2-text-muted)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Each matcher describes which skills are allowed under this intent. At bind time
          the matchers are resolved against the agent's actual skills; the resolved set is
          frozen on the binding (no silent widening — operator rebinds to refresh).
        </p>

        {/* Phase 2B-i — catalog-load state. Loading is a faint inline note,
            failure is a soft warning that keeps the form usable (the picker
            degrades to raw text inputs). */}
        {catalogLoading && (
          <div style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Loader2 size={12} className="animate-spin" />
            <span>Loading skill catalog for picker…</span>
          </div>
        )}
        {catalogLoadError && (
          <div style={{
            padding: '8px 10px', marginBottom: 12, background: 'var(--v2-warning-soft)',
            border: '1px solid var(--v2-warning-soft)', borderRadius: 6, fontSize: 12, color: 'var(--v2-warning)',
          }}>
            Skill picker unavailable ({catalogLoadError}) — falling back to raw text inputs.
            You can still type matcher values manually; explicit-skill UUIDs won't be
            checked against the catalog at save.
          </div>
        )}

        {matchers.length === 0 && (
          <div style={{
            padding: 16, marginBottom: 12, background: 'var(--v2-bg-muted)',
            border: '1px dashed var(--v2-border-strong)', borderRadius: 6,
            fontSize: 13, color: 'var(--v2-text-muted)', textAlign: 'center',
          }}>
            No matchers yet. Add at least one before submitting for review.
          </div>
        )}

        {matchers.map((m, idx) => (
          <MatcherRow
            key={idx}
            matcher={m}
            onChange={(patch) => updateMatcher(idx, patch)}
            onRemove={() => removeMatcher(idx)}
            disabled={submitting}
            skillCatalog={skillCatalog}
            skillIndexById={skillIndexById}
          />
        ))}

        <div style={{ marginBottom: 24 }}>
          <Button
            variant="secondary" size="sm" iconLeft={<Plus />} onClick={addMatcher} disabled={submitting}
          >
            Add matcher
          </Button>
        </div>

        <SectionLabel>Autonomy ceiling</SectionLabel>
        <p style={{ fontSize: 12, color: 'var(--v2-text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
          Optional. Caps the autonomy level of skills called under this intent.
          Reserved for runtime enforcement in a future phase; current build records
          the value but does not yet enforce.
        </p>
        <select
          value={maxAutonomy}
          onChange={(e) => setMaxAutonomy(e.target.value)}
          disabled={submitting}
          style={{ ...inputStyle, marginBottom: 20 }}
        >
          <option value="">(no ceiling)</option>
          <option value="HUMAN_CONTROLLED">HUMAN_CONTROLLED</option>
          <option value="ASSISTED">ASSISTED</option>
          <option value="SUPERVISED">SUPERVISED</option>
          <option value="AUTONOMOUS">AUTONOMOUS</option>
        </select>

        <SectionLabel>Requires human approval</SectionLabel>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--v2-text-primary)',
          cursor: submitting ? 'not-allowed' : 'pointer',
        }}>
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(e) => setRequiresApproval(e.target.checked)}
            disabled={submitting}
          />
          <span>Calls under this intent require explicit human approval before execution.</span>
        </label>

        <div style={{ height: 24 }} />
        <SectionLabel>Intent Rego policy (fine gate)</SectionLabel>
        <p style={{ fontSize: 12, color: 'var(--v2-text-muted)', margin: '0 0 8px', lineHeight: 1.5 }}>
          The contextual authorization layer. The matchers above are the fast allowlist; this
          OPA/Rego policy decides over the full call context (skill, classification, autonomy…).
          Package must be <code style={{ fontFamily: 'ui-monospace, monospace' }}>agentlets.intent</code> with
          an <code style={{ fontFamily: 'ui-monospace, monospace' }}>allow</code> rule. Required before this
          version can be approved.
        </p>
        <div style={{ marginBottom: 8 }}>
          <Button
            variant="secondary" size="sm" iconLeft={<Wand2 />} onClick={scaffoldRego}
            disabled={submitting || (!!intentRego && intentRego.trim() !== '')}
          >
            Insert agentlets.intent scaffold
          </Button>
        </div>
        <textarea
          value={intentRego}
          onChange={(e) => setIntentRego(e.target.value)}
          disabled={submitting}
          spellCheck={false}
          rows={12}
          placeholder={'package agentlets.intent\ndefault allow = false\nallow { ... }'}
          style={{
            ...inputStyle, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            fontSize: 12.5, lineHeight: 1.5, resize: 'vertical', marginBottom: 16,
          }}
        />

        {/* Dry-run simulation — runs the current Rego text against a sample input. */}
        <div style={{ border: '1px solid var(--v2-border-default)', borderRadius: 6, padding: 12, marginBottom: 8 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 12, fontWeight: 600,
            color: 'var(--v2-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>
            <FlaskConical size={13} /> <span>Dry-run this policy</span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
            <div style={{ flex: '1 1 160px' }}>
              <label style={miniLabel}>skill</label>
              <input
                value={dryInput.skill}
                onChange={(e) => setDryInput((p) => ({ ...p, skill: e.target.value }))}
                placeholder="org.read.headcount" disabled={submitting}
                style={{ ...inputStyle, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
              />
            </div>
            <div style={{ flex: '0 1 130px' }}>
              <label style={miniLabel}>data_class</label>
              <input
                value={dryInput.dataClass}
                onChange={(e) => setDryInput((p) => ({ ...p, dataClass: e.target.value }))}
                placeholder="internal" disabled={submitting} style={{ ...inputStyle, width: '100%' }}
              />
            </div>
            <div style={{ flex: '0 1 160px' }}>
              <label style={miniLabel}>autonomy</label>
              <select
                value={dryInput.autonomy}
                onChange={(e) => setDryInput((p) => ({ ...p, autonomy: e.target.value }))}
                disabled={submitting} style={{ ...inputStyle, width: '100%' }}
              >
                <option value="">(none)</option>
                <option value="HUMAN_CONTROLLED">HUMAN_CONTROLLED</option>
                <option value="ASSISTED">ASSISTED</option>
                <option value="SUPERVISED">SUPERVISED</option>
                <option value="AUTONOMOUS">AUTONOMOUS</option>
              </select>
            </div>
            <Button
              variant="secondary" size="sm"
              iconLeft={drying ? <Loader2 className="animate-spin" /> : <Play />}
              onClick={runDryRun} disabled={submitting || drying || !intentRego.trim()}
            >
              {drying ? 'Running…' : 'Simulate'}
            </Button>
          </div>
          {dryResult && <DryRunVerdict result={dryResult} />}
          <p style={{ fontSize: 11, color: 'var(--v2-text-muted)', margin: '8px 0 0', lineHeight: 1.45 }}>
            allowed_skills is derived from this version's skill-name matchers. Nothing is saved — this runs your
            current Rego text against the sample via OPA.
          </p>
        </div>
      </div>
    </Slideout>
  );
}

/* J5 — canonical scaffold inserted into an empty Rego editor. */
const REGO_SCAFFOLD = `package agentlets.intent
# Intent fine gate. The runtime passes:
#   input.skill          requested skill name
#   input.allowed_skills binding's resolved allowlist (literal names)
#   input.intent         declared intent
#   input.autonomy       intent autonomy ceiling
#   input.risk_tier      template risk tier
#   input.data_class     caller data classification (may be null)
#   input.args           raw caller arguments
default allow = false

allow {
    input.skill == input.allowed_skills[_]
    input.data_class != "restricted"
}
`;

/* J5 — dry-run verdict chip. POLICY_CONFLICT (an authoring defect that comes back as kind=DENY)
 * is shown distinctly via the shared regoVerdictView, not as a plain "DENY". */
function DryRunVerdict({ result }) {
  const view = regoVerdictView(result.kind, result.failure);
  const s = REGO_TONE_COLORS[view.tone] || REGO_TONE_COLORS.warn;
  const hint = regoConflictHint(result.failure);
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px',
      background: s.bg, borderRadius: 6, fontSize: 12.5, color: s.color, lineHeight: 1.45,
    }}>
      <span style={{ fontWeight: 700, flexShrink: 0 }}>{view.label}</span>
      <span style={{ color: 'var(--v2-text-secondary)' }}>{result.reason}{hint && (result.reason ? ' — ' : '') + hint}</span>
    </div>
  );
}

const miniLabel = { display: 'block', fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 3 };

/* ── matcher row ────────────────────────────────────────────────── */
/**
 * Phase 2B-i — three-mode matcher row with skill-catalog-aware pickers.
 *
 * Mode dropdown (left) stays unchanged. The value cell on the right is now
 * mode-specific:
 *   explicit-skill → ExplicitSkillCombobox: search by name, store UUID
 *   skill-name     → SkillNameAutocomplete: type-ahead literal names
 *   skill-glob     → SkillGlobInput: free-text + live "matches N skills"
 *
 * When the catalog failed to load (skillCatalog is empty), every variant
 * falls back to a plain text input. The form stays usable in degraded
 * mode (the parent's amber catalog-error banner explains why).
 */
function MatcherRow({ matcher, onChange, onRemove, disabled, skillCatalog, skillIndexById }) {
  return (
    <div style={{
      display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start',
    }}>
      <select
        value={matcher.mode}
        onChange={(e) => onChange({ mode: e.target.value, value: '' })}
        disabled={disabled}
        style={{ ...inputStyle, width: 160, flexShrink: 0 }}
        title="Switching modes clears the value so the wrong-shaped data isn't carried over (e.g. a UUID into skill-name mode)."
      >
        <option value="explicit-skill">explicit-skill</option>
        <option value="skill-name">skill-name</option>
        <option value="skill-glob">skill-glob</option>
      </select>

      <div style={{ flex: 1, minWidth: 0 }}>
        {matcher.mode === 'explicit-skill' && (
          <ExplicitSkillCombobox
            value={matcher.value}
            onChange={(v) => onChange({ value: v })}
            disabled={disabled}
            skillCatalog={skillCatalog}
            skillIndexById={skillIndexById}
          />
        )}
        {matcher.mode === 'skill-name' && (
          <SkillNameAutocomplete
            value={matcher.value}
            onChange={(v) => onChange({ value: v })}
            disabled={disabled}
            skillCatalog={skillCatalog}
          />
        )}
        {matcher.mode === 'skill-glob' && (
          <SkillGlobInput
            value={matcher.value}
            onChange={(v) => onChange({ value: v })}
            disabled={disabled}
            skillCatalog={skillCatalog}
          />
        )}
      </div>

      <button
        onClick={onRemove}
        disabled={disabled}
        title="Remove matcher"
        style={{
          padding: 8, background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-strong)',
          borderRadius: 6, color: 'var(--v2-danger)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          alignSelf: 'flex-start',
        }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Phase 2B-i — explicit-skill combobox
 *
 * The matcher value is a UUID, but authors think in friendly names. This
 * widget renders:
 *   - a text input where the user types a name fragment
 *   - a popover list filtered live against the catalog
 *   - "send_email (Agent A)" disambiguation when one name lives on
 *     multiple agents
 * Selection stores the UUID in `value`; the input box then displays the
 * friendly name. If `value` is a UUID that DOESN'T match any catalog row
 * (e.g. the skill was deleted, or the version was authored before the
 * catalog endpoint existed), we show the raw UUID with an amber "unknown
 * skill" marker so the author knows to revisit.
 * ────────────────────────────────────────────────────────────────── */
function ExplicitSkillCombobox({ value, onChange, disabled, skillCatalog, skillIndexById }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  // Catalog-empty degraded mode → raw UUID input.
  if (!Array.isArray(skillCatalog) || skillCatalog.length === 0) {
    return (
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="a1b2c3d4-...-uuid (catalog unavailable — type manually)"
        disabled={disabled}
        style={{ ...inputStyle, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
      />
    );
  }

  const selected = value && skillIndexById?.get(value);
  const valueIsUuid = isUuid(value);
  // "Unknown UUID" = author typed/pasted a UUID that's not in the catalog.
  // Treat as a warning, not an error — they may still want to keep it
  // (the save-time resolvability check will surface the same).
  const unknownUuid = valueIsUuid && !selected;

  // Filter against name + agent name. Case-insensitive substring match;
  // 200-row cap on the rendered popover to keep the DOM small. Authors
  // refine via additional typing rather than scrolling 1000 rows.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skillCatalog.slice(0, 200);
    const out = [];
    for (const s of skillCatalog) {
      const hay = `${s.name} ${s.agentName || ''}`.toLowerCase();
      if (hay.includes(q)) {
        out.push(s);
        if (out.length >= 200) break;
      }
    }
    return out;
  }, [query, skillCatalog]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const choose = (s) => {
    onChange(s.id);
    setQuery('');
    setOpen(false);
  };

  // Displayed input value: friendly name when we have a match, raw UUID
  // when the value is a UUID with no catalog hit, query string while
  // typing (open=true).
  const displayValue = open
    ? query
    : (selected ? `${selected.name}${selected.agentName ? ` · ${selected.agentName}` : ''}` : (value || ''));

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{
          position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--v2-text-muted)', pointerEvents: 'none',
        }} />
        <input
          ref={inputRef}
          value={displayValue}
          onChange={(e) => { setQuery(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search skills by name…"
          disabled={disabled}
          style={{
            ...inputStyle, width: '100%', paddingLeft: 30, paddingRight: 30,
            fontFamily: 'inherit',
          }}
        />
        <ChevronDown size={14} style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          color: 'var(--v2-text-muted)', pointerEvents: 'none',
        }} />
      </div>

      {/* Unknown-UUID warning chip. Not an error — author may keep it. */}
      {!open && unknownUuid && (
        <div style={{
          marginTop: 4, fontSize: 11, color: 'var(--v2-warning)',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <AlertTriangle size={11} />
          <span>UUID not in your skill catalog — save will reject unless this is corrected.</span>
        </div>
      )}

      {open && (
        <div ref={popoverRef} style={popoverStyle}>
          {filtered.length === 0 ? (
            <div style={emptyHintStyle}>No skills match "{query}". Type a different fragment, or select skill-name mode to enter a literal that resolves later.</div>
          ) : (
            filtered.map((s) => {
              const isSelected = s.id === value;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => choose(s)}
                  style={{
                    ...popoverItemStyle,
                    background: isSelected ? 'var(--v2-accent-cyan-soft)' : 'var(--v2-bg-surface)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {isSelected && <Check size={12} color="var(--v2-accent-cyan)" />}
                    {!isSelected && <span style={{ width: 12 }} />}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0 }}>
                      <span style={{ fontSize: 13, color: 'var(--v2-text-primary)', fontWeight: isSelected ? 600 : 500 }}>{s.name}</span>
                      {s.agentName && (
                        <span style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>{s.agentName}</span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Phase 2B-i — skill-name type-ahead
 *
 * The matcher value is a literal skill name (resolved at bind time).
 * The picker offers existing names as suggestions but DOES NOT restrict
 * to them — authors may legitimately type a future skill name that the
 * agent will gain at bind time. Selection sets the literal string.
 *
 * Names are deduplicated case-insensitively (skill catalog has one row
 * per skill row, but many skills may share a name across agents).
 * ────────────────────────────────────────────────────────────────── */
function SkillNameAutocomplete({ value, onChange, disabled, skillCatalog }) {
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const popoverRef = useRef(null);

  const distinctNames = useMemo(() => {
    if (!Array.isArray(skillCatalog)) return [];
    const seen = new Set();
    const out = [];
    for (const s of skillCatalog) {
      if (!s?.name) continue;
      const key = s.name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(s.name);
    }
    return out;
  }, [skillCatalog]);

  const filtered = useMemo(() => {
    const q = (value || '').trim().toLowerCase();
    if (!q) return distinctNames.slice(0, 50);
    const out = [];
    for (const n of distinctNames) {
      if (n.toLowerCase().includes(q)) {
        out.push(n);
        if (out.length >= 50) break;
      }
    }
    return out;
  }, [value, distinctNames]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e) {
      if (popoverRef.current && popoverRef.current.contains(e.target)) return;
      if (inputRef.current && inputRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="send_email"
        disabled={disabled}
        style={{ ...inputStyle, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
      />
      {open && filtered.length > 0 && (
        <div ref={popoverRef} style={popoverStyle}>
          {filtered.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => { onChange(n); setOpen(false); }}
              style={popoverItemStyle}
            >
              <span style={{ fontSize: 13, color: 'var(--v2-text-primary)', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                {n}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 * Phase 2B-i — skill-glob with live "matches N" preview
 *
 * The matcher value is a glob pattern (e.g. "lookup_*", "*.refresh").
 * We render a free-text input and compute, on every keystroke, how
 * many distinct skill NAMES in the catalog would match. This is a
 * client-side preview only — the authoritative match happens at bind
 * time against the agent's actual skill set.
 *
 * Implementation: tiny glob → regex compiler (only `*` and `?` are
 * supported, matching IntentMatcher.matchAnyPattern on the backend).
 * If the pattern is malformed we surface that as a soft hint, not an
 * error — backend will validate shape at save.
 * ────────────────────────────────────────────────────────────────── */
function SkillGlobInput({ value, onChange, disabled, skillCatalog }) {
  const { matchCount, regexError } = useMemo(() => {
    const pat = (value || '').trim();
    if (!pat || !Array.isArray(skillCatalog) || skillCatalog.length === 0) {
      return { matchCount: null, regexError: null };
    }
    let re;
    try {
      re = globToRegex(pat);
    } catch (err) {
      return { matchCount: null, regexError: String(err.message || err) };
    }
    // Distinct name match — same dedupe rule as skill-name picker so
    // authors get a count of unique skill names, not catalog rows.
    const distinct = new Set();
    for (const s of skillCatalog) {
      if (s?.name && re.test(s.name)) distinct.add(s.name.toLowerCase());
    }
    return { matchCount: distinct.size, regexError: null };
  }, [value, skillCatalog]);

  return (
    <div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="lookup_*"
        disabled={disabled}
        style={{ ...inputStyle, width: '100%', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
      />
      {(matchCount != null || regexError) && (
        <div style={{
          marginTop: 4, fontSize: 11,
          color: regexError ? 'var(--v2-warning)' : (matchCount === 0 ? 'var(--v2-warning)' : 'var(--v2-text-secondary)'),
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {regexError ? (
            <><AlertTriangle size={11} /><span>Pattern error: {regexError}</span></>
          ) : matchCount === 0 ? (
            <><AlertTriangle size={11} /><span>Matches 0 skills in your catalog right now. The pattern will still apply at bind time if matching skills appear later.</span></>
          ) : (
            <span>Matches {matchCount} skill{matchCount === 1 ? '' : 's'} in your catalog right now (resolved per-agent at bind time).</span>
          )}
        </div>
      )}
    </div>
  );
}

/* Glob → JS regex. Only `*` and `?` are special, matching backend
 * IntentMatcher.matchAnyPattern. Anchored with ^...$ for full-name
 * comparison. Other regex metachars are escaped so e.g. "policy.refresh"
 * doesn't allow "policyXrefresh".
 */
function globToRegex(glob) {
  let re = '';
  for (let i = 0; i < glob.length; i++) {
    const c = glob[i];
    if (c === '*') re += '.*';
    else if (c === '?') re += '.';
    else if ('.+^$()[]{}|\\'.includes(c)) re += '\\' + c;
    else re += c;
  }
  return new RegExp('^' + re + '$', 'i');
}

/* Shared popover + item styles for the two combobox variants. */
const popoverStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
  marginTop: 4, maxHeight: 240, overflowY: 'auto',
  background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-strong)',
  borderRadius: 6, boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
};

const popoverItemStyle = {
  display: 'block', width: '100%', padding: '8px 10px',
  textAlign: 'left', background: 'var(--v2-bg-surface)', border: 'none',
  borderBottom: '1px solid var(--v2-bg-muted)', cursor: 'pointer',
};

const emptyHintStyle = {
  padding: '10px 12px', fontSize: 12, color: 'var(--v2-text-muted)', lineHeight: 1.4,
};

/* ── helpers ────────────────────────────────────────────────────── */
function toFormRow(raw) {
  // BE may return as {mode, value} OR as a structured node. Normalise.
  if (raw && typeof raw === 'object') {
    return { mode: String(raw.mode || 'skill-name'), value: String(raw.value || '') };
  }
  return { mode: 'skill-name', value: String(raw || '') };
}

function isUuid(s) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(s).trim());
}

/* ── primitives ─────────────────────────────────────────────────── */
const inputStyle = {
  padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--v2-border-strong)', borderRadius: 6, background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-primary)', outline: 'none', fontFamily: 'inherit',
};

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 600, color: 'var(--v2-text-secondary)',
      marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>{children}</div>
  );
}

function InfoBanner({ kind, children }) {
  const styles = {
    error: { color: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' },
    warn:  { color: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' },
  };
  const s = styles[kind] || styles.warn;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px', marginBottom: 16,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 6, fontSize: 12, lineHeight: 1.4,
    }}>
      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}
